import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import CloseIcon from '@src/svgs/CloseIcon';
import EditSparkleIcon from '@src/svgs/EditSparkleIcon';
import SparkleIcon from '@src/svgs/SparkleIcon';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import ChatGPTService from '../../../services/ChatGPTService';
import { useDatoClient } from '../../../stores-v2/StoreContext';
import { useVideoCreatorStore } from '../../../stores-v2/VideoCreatorStoreContext';

import type { TranscriptEditorStore } from '../../TranscriptEditorStore';

interface SpeakerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editorStore: TranscriptEditorStore;
  // For editing mode
  speakerToEdit?: {
    speakerLabel: string;
    datoSpeakerId: string;
    name: string;
    title?: string;
    bio?: string;
    voice?: string;
  };
  modalOptions?: {
    addToStory: boolean;
    addToOrganization: boolean;
  };
  skipAssignment?: boolean; // For new speaker creation
}

/**
 * Unified modal for creating new speakers or editing existing ones
 */
const SpeakerFormModal: React.FC<SpeakerFormModalProps> = ({
  isOpen,
  onClose,
  editorStore,
  speakerToEdit,
  skipAssignment = false,
  modalOptions,
}) => {
  const isEditMode = !!speakerToEdit;

  // Initialize state with either existing speaker data or empty values
  const [name, setName] = useState(speakerToEdit?.name || '');
  const [title, setTitle] = useState(speakerToEdit?.title || '');
  const [bio, setBio] = useState(speakerToEdit?.bio || '');
  const [voice, setVoice] = useState(speakerToEdit?.voice || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingBio, setIsGeneratingBio] = useState(false);
  const [showRegenerateInput, setShowRegenerateInput] = useState(false);
  const [regenerateInstructions, setRegenerateInstructions] = useState('');
  const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
  const [showRegenerateVoiceInput, setShowRegenerateVoiceInput] =
    useState(false);
  const [regenerateVoiceInstructions, setRegenerateVoiceInstructions] =
    useState('');

  // Get videoCreator and create ChatGPTService instance
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const chatGPTServiceRef = useRef<ChatGPTService | null>(null);

  // Initialize ChatGPTService when component mounts
  useEffect(() => {
    if (videoCreator && datoClientStore?.aiPromptRepository) {
      chatGPTServiceRef.current = new ChatGPTService(
        videoCreator,
        datoClientStore.aiPromptRepository,
      );
    }
  }, [videoCreator, datoClientStore]);

  // Add refs for modal container and first input
  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Handle escape key, auto-focus, and event propagation
  useEffect(() => {
    if (!isOpen) return;

    // Handle escape key to close modal
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Auto-focus the name input when modal opens
    if (nameInputRef.current) {
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 50);
    }

    // Prevent selection changes when interacting with the modal
    const handleModalEvents = (e: Event) => {
      e.stopPropagation(); // Prevent event from bubbling up
    };

    // Get the modal and overlay elements
    const modalElement = modalRef.current;
    const overlayElement = modalElement?.parentElement; // The ModalOverlay is parent of ModalContent

    // Add event listeners to the modal content
    if (modalElement) {
      modalElement.addEventListener('mousedown', handleModalEvents, true);
      modalElement.addEventListener('mouseup', handleModalEvents, true);
      modalElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // Add event listeners to the modal overlay
    if (overlayElement) {
      overlayElement.addEventListener('mousedown', handleModalEvents, true);
      overlayElement.addEventListener('mouseup', handleModalEvents, true);
      overlayElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // Add escape key handler
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      // Clean up all event listeners
      document.removeEventListener('keydown', handleEscapeKey);

      // Clean up modal content listeners
      if (modalElement) {
        modalElement.removeEventListener('mousedown', handleModalEvents, true);
        modalElement.removeEventListener('mouseup', handleModalEvents, true);
        modalElement.removeEventListener('mousemove', handleModalEvents, true);
      }

      // Clean up overlay listeners
      if (overlayElement) {
        overlayElement.removeEventListener(
          'mousedown',
          handleModalEvents,
          true,
        );
        overlayElement.removeEventListener('mouseup', handleModalEvents, true);
        overlayElement.removeEventListener(
          'mousemove',
          handleModalEvents,
          true,
        );
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Speaker name is required');
      return;
    }

    if (!editorStore) {
      setError('Editor store not available');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const speakerService = editorStore.editor?.getSpeakerService();
      if (!speakerService) {
        throw new Error('Speaker service not available');
      }

      if (isEditMode && speakerToEdit) {
        if (speakerToEdit.datoSpeakerId) {
          // Update existing Dato speaker with all form fields
          const success = await speakerService.updateSpeakerProperties(
            speakerToEdit.datoSpeakerId,
            {
              name,
              title,
              bio,
              voice,
            },
          );

          if (!success) {
            throw new Error('Failed to update speaker');
          }
        } else {
          // This is an anonymous speaker (has speakerLabel but no datoSpeakerId)
          // We can use the built-in createAndAssignSpeaker method with the existingSpeakerLabel parameter

          if (speakerToEdit.speakerLabel) {
            // Use the standard method with the existingSpeakerLabel parameter
            // When addToStory is true, we should NOT skip story addition
            const skipStoryAddition = modalOptions?.addToStory === false;

            const success = await editorStore.createAndAssignSpeaker(
              name,
              title,
              bio,
              voice,
              false, // Don't skip assignment
              skipStoryAddition,
              speakerToEdit.speakerLabel, // Pass the anonymous speaker label
            );

            if (!success) {
              throw new Error(
                'Failed to create and assign speaker to anonymous speaker',
              );
            }
          } else {
            throw new Error('Missing speaker label for anonymous speaker');
          }
        }
      } else {
        // Create new speaker
        // When addToStory is true, we should NOT skip story addition
        const skipStoryAddition = modalOptions?.addToStory === false;
        console.log(
          'creating new speaker',
          skipStoryAddition,
          skipAssignment,
          modalOptions,
        );

        // Use the standard method if no custom options
        const success = await editorStore.createAndAssignSpeaker(
          name,
          title,
          bio,
          voice,
          skipAssignment,
          skipStoryAddition,
        );

        if (!success) {
          throw new Error(
            skipAssignment
              ? 'Failed to create speaker'
              : 'Failed to create and assign speaker',
          );
        }
      }

      // Reset form and close modal on success
      setName('');
      setTitle('');
      setBio('');
      setVoice('');
      onClose();
    } catch (err) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'creating'} speaker:`,
        err,
      );
      setError(
        `Failed to ${
          isEditMode ? 'update' : 'create'
        } speaker. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateBio = async (customInstructions?: string) => {
    if (!name || !chatGPTServiceRef.current) return;

    setIsGeneratingBio(true);
    setError(null);

    try {
      // Get all speakers from the editor store
      const speakerService = editorStore.editor?.getSpeakerService();
      let allSpeakers: any[] = [];

      if (speakerService) {
        const speakers = speakerService.getAllSpeakers(
          editorStore.editor?.getContainer(),
          editorStore,
        );
        // Combine all speaker categories to get a complete list
        allSpeakers = [
          ...speakers.mapped,
          ...speakers.unmapped,
          ...speakers.story,
          ...speakers.album,
        ].filter(
          (speaker, index, self) =>
            // Remove duplicates based on speakerLabel
            index ===
            self.findIndex(s => s.speakerLabel === speaker.speakerLabel),
        );
      }

      const generatedBio = await chatGPTServiceRef.current.generateSpeakerBio({
        speakerName: name,
        speakerRole: title || undefined,
        selectedSpeakers: allSpeakers,
        customInstructions,
        existingBio: customInstructions ? bio : undefined, // Pass existing bio when regenerating
      });

      if (generatedBio) {
        setBio(generatedBio);
        // Reset regeneration UI after successful regeneration
        if (customInstructions) {
          setShowRegenerateInput(false);
          setRegenerateInstructions('');
        }
      }
    } catch (err) {
      console.error('Error generating bio:', err);
      setError('Failed to generate bio. Please try again.');
    } finally {
      setIsGeneratingBio(false);
    }
  };

  const handleGenerateVoice = async (customInstructions?: string) => {
    if (!name || !chatGPTServiceRef.current) return;

    setIsGeneratingVoice(true);
    setError(null);

    try {
      // Get all speakers from the editor store
      const speakerService = editorStore.editor?.getSpeakerService();
      let allSpeakers: any[] = [];

      if (speakerService) {
        const speakers = speakerService.getAllSpeakers(
          editorStore.editor?.getContainer(),
          editorStore,
        );
        // Combine all speaker categories to get a complete list
        allSpeakers = [
          ...speakers.mapped,
          ...speakers.unmapped,
          ...speakers.story,
          ...speakers.album,
        ].filter(
          (speaker, index, self) =>
            // Remove duplicates based on speakerLabel
            index ===
            self.findIndex(s => s.speakerLabel === speaker.speakerLabel),
        );
      }

      const generatedVoice =
        await chatGPTServiceRef.current.generateSpeakerVoice({
          speakerName: name,
          speakerRole: title || undefined,
          selectedSpeakers: allSpeakers,
          customInstructions,
          existingVoice: customInstructions ? voice : undefined, // Pass existing voice when regenerating
        });

      if (generatedVoice) {
        setVoice(generatedVoice);
        // Reset regeneration UI after successful regeneration
        if (customInstructions) {
          setShowRegenerateVoiceInput(false);
          setRegenerateVoiceInstructions('');
        }
      }
    } catch (err) {
      console.error('Error generating voice:', err);
      setError('Failed to generate voice description. Please try again.');
    } finally {
      setIsGeneratingVoice(false);
    }
  };

  return (
    <ModalOverlay
      onClick={e => {
        // Close the modal when clicking the overlay background
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      data-selection-menu="true"
      style={{ userSelect: 'none' }}
      contentEditable={false}
    >
      <ModalContent
        ref={modalRef}
        data-selection-menu="true"
        style={{ userSelect: 'none' }}
        contentEditable={false}
      >
        <ModalHeader>
          <h3>
            {isEditMode
              ? speakerToEdit?.datoSpeakerId
                ? 'Edit Speaker'
                : 'Create & Assign Speaker'
              : 'Create New Speaker'}
          </h3>
          <h2>Speaker Details</h2>
          <CloseButton
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
          >
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <form
            onSubmit={e => {
              e.stopPropagation(); // Don't let the submit event propagate
              handleSubmit(e);
            }}
          >
            <FormGroup>
              <Label htmlFor="speakerName">Name</Label>
              <Input
                id="speakerName"
                ref={nameInputRef}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter speaker name"
                autoFocus
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="speakerTitle">Title/Role</Label>
              <Input
                id="speakerTitle"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. CEO, Product Manager, etc."
              />
            </FormGroup>

            <FormGroup>
              <BioLabelContainer>
                <Label htmlFor="speakerBio">Biography</Label>
                <ButtonContainer>
                  {!bio &&
                    (isGeneratingBio ? (
                      <SparkleLoading />
                    ) : (
                      <GenerateBioButton
                        type="button"
                        onClick={() => handleGenerateBio()}
                        disabled={isGeneratingBio || isGeneratingVoice}
                      >
                        <EditSparkleIcon />
                        Generate
                      </GenerateBioButton>
                    ))}
                  {bio && !showRegenerateInput && (
                    <GenerateBioButton
                      type="button"
                      onClick={() => setShowRegenerateInput(true)}
                      disabled={isGeneratingBio || isGeneratingVoice}
                    >
                      <EditSparkleIcon />
                      Regenerate
                    </GenerateBioButton>
                  )}
                </ButtonContainer>
              </BioLabelContainer>
              <Textarea
                id="speakerBio"
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="Brief biography of the speaker"
                rows={3}
                disabled={isGeneratingBio || isGeneratingVoice}
              />
              {showRegenerateInput && (
                <RegenerateContainer>
                  <RegenerateInput
                    value={regenerateInstructions}
                    onChange={e => setRegenerateInstructions(e.target.value)}
                    placeholder="How would you like to improve the bio? (e.g., 'Make it shorter', 'Focus more on recent work', 'Add more personal details')"
                    disabled={isGeneratingBio || isGeneratingVoice}
                  />
                  <RegenerateActions>
                    <RegenerateButton
                      type="button"
                      onClick={() => handleGenerateBio(regenerateInstructions)}
                      disabled={
                        isGeneratingBio || !regenerateInstructions.trim()
                      }
                    >
                      {isGeneratingBio ? 'Regenerating...' : 'Regenerate'}
                    </RegenerateButton>
                    <CancelRegenerateButton
                      type="button"
                      onClick={() => {
                        setShowRegenerateInput(false);
                        setRegenerateInstructions('');
                      }}
                      disabled={isGeneratingBio || isGeneratingVoice}
                    >
                      Cancel
                    </CancelRegenerateButton>
                  </RegenerateActions>
                </RegenerateContainer>
              )}
            </FormGroup>

            <FormGroup>
              <VoiceLabelContainer>
                <Label htmlFor="speakerVoice">Voice Description</Label>
                <ButtonContainer>
                  {!voice &&
                    (isGeneratingVoice ? (
                      <SparkleLoading />
                    ) : (
                      <GenerateBioButton
                        type="button"
                        onClick={() => handleGenerateVoice()}
                        disabled={isGeneratingBio || isGeneratingVoice}
                      >
                        <EditSparkleIcon />
                        Generate
                      </GenerateBioButton>
                    ))}
                  {voice && !showRegenerateVoiceInput && (
                    <GenerateBioButton
                      type="button"
                      onClick={() => setShowRegenerateVoiceInput(true)}
                      disabled={isGeneratingBio || isGeneratingVoice}
                    >
                      <EditSparkleIcon />
                      Regenerate
                    </GenerateBioButton>
                  )}
                </ButtonContainer>
              </VoiceLabelContainer>
              <Textarea
                id="speakerVoice"
                value={voice}
                onChange={e => setVoice(e.target.value)}
                placeholder="Describe speaker’s voice – tone, style, etc."
                rows={3}
                disabled={isGeneratingBio || isGeneratingVoice}
              />
              {showRegenerateVoiceInput && (
                <RegenerateContainer>
                  <RegenerateInput
                    value={regenerateVoiceInstructions}
                    onChange={e =>
                      setRegenerateVoiceInstructions(e.target.value)
                    }
                    placeholder="How would you like to improve the voice description? (e.g., 'More casual tone', 'Include speaking pace', 'Add humor style')"
                    disabled={isGeneratingBio || isGeneratingVoice}
                  />
                  <RegenerateActions>
                    <RegenerateButton
                      type="button"
                      onClick={() =>
                        handleGenerateVoice(regenerateVoiceInstructions)
                      }
                      disabled={
                        isGeneratingVoice || !regenerateVoiceInstructions.trim()
                      }
                    >
                      {isGeneratingVoice ? 'Regenerating...' : 'Regenerate'}
                    </RegenerateButton>
                    <CancelRegenerateButton
                      type="button"
                      onClick={() => {
                        setShowRegenerateVoiceInput(false);
                        setRegenerateVoiceInstructions('');
                      }}
                      disabled={isGeneratingBio || isGeneratingVoice}
                    >
                      Cancel
                    </CancelRegenerateButton>
                  </RegenerateActions>
                </RegenerateContainer>
              )}
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonGroup>
              <CancelButton
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onClose();
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Cancel
              </CancelButton>
              <SubmitButton
                type="submit"
                disabled={isSubmitting}
                onClick={e => {
                  e.stopPropagation();
                  // Don't preventDefault as we want form submission to occur
                }}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </SubmitButton>
            </ButtonGroup>
          </form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

const SparkleLoading = () => (
  <SimpleLoadingSpinner
    spinnerWidth={3.5}
    spinnerSize={24}
    icon={<SparkleIcon />}
    iconSize={10}
    text={null}
  />
);

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000003;
`;

const ModalContent = styled.div`
  background-color: #03041a;
  width: 500px;
  max-width: 90vw;
  border: 1px solid #484848;
  box-shadow: 8px 16px 8px 0 rgba(0, 0, 0, 0.4);
  padding: 32px 24px;
  position: relative;
  border-radius: 8px;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  margin-bottom: 12px;
  h3 {
    color: #45d483;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 1.92px;
    text-transform: uppercase;
    margin: 0;
  }
  h2 {
    color: #f3e9d7;
    font-size: 20px;
    font-weight: 700;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  right: 24px;
  top: 24px;
  background: none;
  border: none;
  color: #a9a9a9;
  font-size: 24px;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #ffffff;
  }
`;

const ModalBody = styled.div`
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  color: #f3e9d7;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 8px 16px;
  background: none;
  border-radius: 8px;
  border: 1px solid #848484;
  color: #f3e9d7;
  font-size: 14px;
  line-height: 150%;
  &::placeholder {
    color: #848484;
  }
  &:focus {
    outline: none;
    border-color: #f2d093;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 128px;
  padding: 8px 16px;
  background: none;
  border-radius: 8px;
  border: 1px solid #848484;
  color: #f3e9d7;
  font-size: 14px;
  line-height: 150%;
  resize: vertical;
  &::placeholder {
    color: #848484;
  }
  &:focus {
    outline: none;
    border-color: #f2d093;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button`
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  gap: 8px;
  flex: 1 0 0;
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    filter: brightness(1.1) saturate(1.1);
  }
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  border: 1px solid #17c964;
  color: #17c964;
`;

const SubmitButton = styled(Button)`
  background-color: #17c964;
  border: 1px solid #17c964;
  color: #03041a;

  &:disabled {
    background-color: #6c6c6c;
    border-color: #6c6c6c;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6961;
  font-size: 14px;
  margin-top: 8px;
`;

const BioLabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const VoiceLabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const GenerateBioButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 110px;
  height: 30px;
  background: none;
  border: 1px solid #484848;
  font-size: 12px;
  border-radius: 8px;
  color: #17c964;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #17c964;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const RegenerateContainer = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RegenerateInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 8px 16px;
  background: none;
  border-radius: 8px;
  border: 1px solid #848484;
  color: #f3e9d7;
  font-size: 14px;
  line-height: 150%;
  resize: vertical;
  &::placeholder {
    color: #848484;
  }
  &:focus {
    outline: none;
    border-color: #f2d093;
  }
`;

const RegenerateActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const RegenerateButton = styled.button`
  background-color: #f2d093;
  border: 1px solid #f2d093;
  color: #030419;
  padding: 6px 14px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover:not(:disabled) {
    background-color: #d9b77e;
  }

  &:disabled {
    background-color: #6c6c6c;
    border-color: #6c6c6c;
    cursor: not-allowed;
  }
`;

const CancelRegenerateButton = styled.button`
  background-color: transparent;
  border: 1px solid #484848;
  color: #f3e9d7;
  padding: 6px 14px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: rgba(242, 208, 147, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default SpeakerFormModal;
